import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { X, Info } from 'lucide-react';
import WizardDialog from './WizardDialog';
import ProductAccordionTree from './ProductAccordionTree';
import { buildProductTree } from './productTree';
import { buildUserSchema, type UserFormValues } from '../schemas/user.schema';
import { USER_ROLE_LABELS, type WizardUser } from '../types';
import { useWizard } from '../WizardProvider';
import { registerTrimmed } from '../trimOnBlur';
import { useProducts } from '../../../../hooks/useProducts';
import { checkUserEmailUnique } from '../../../../api/lenderOnboarding';
import {
  EMAIL_DUPLICATE,
  ML_CONSUMER_USER_NAME_DUPLICATE,
  STEP4_ACCESS_HINT,
  STEP4_ACCESS_ON_TEXT,
  EDIT_PRODUCT_ACCESS_LABEL,
  DISCARD_ACCESS_TITLE,
  DISCARD_ACCESS_BODY,
  DISCARD_ACCESS_CONFIRM,
  DISCARD_ACCESS_CANCEL,
  ADD_USER_TITLE,
  EDIT_USER_TITLE,
  EDIT_USER_CONFIRM,
} from '../copy';

interface UserCardProps {
  user: WizardUser;
  /** Just created by Add User: titles the dialog and labels its primary action. */
  isNew: boolean;
  onChange: (data: Partial<Omit<WizardUser, 'id'>>) => void;
  /** Close the dialog — the X and the primary action both end here; the step decides what
   * closing means for a never-completed new user. */
  onClose: () => void;
  /** The editor's verdict, lifted to the step. Reported true while the dialog is open and,
   * on unmount, left at whatever the last verdict was — a row closed while invalid or
   * duplicate keeps holding the step's Next gate. */
  onBlockedChange?: (blocked: boolean) => void;
}

/**
 * The user editor, presented as a dialog over the Team Setup table (the design's Add User /
 * Edit User popup). Field changes commit to the wizard state on every keystroke, same as the
 * other steps — the primary action just closes the dialog, it is not the door the data passes
 * through.
 */
export default function UserCard({ user, isNew, onChange, onClose, onBlockedChange }: UserCardProps) {
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);
  const [duplicate, setDuplicate] = useState(false);
  const [checkedEmail, setCheckedEmail] = useState<string | null>(null);

  // OM-3946 Product Access. user.productAccess has two states that mean opposite things: null =
  // inherit every product the lender enabled in Step 3 (the default); a Record = restricted to
  // only the entries set true. {} is NOT the same as null — {} means "access to nothing".
  const { state, dispatch } = useWizard();

  // Drives both the field's visibility and its validation, so the two can never disagree.
  const mlConsumerSelected = state.losIntegrations.some((los) => los.type === 'ml-consumer');
  const mlMortgageSelected = state.losIntegrations.some((los) => los.type === 'ml-mortgage');
  const schema = useMemo(
    () =>
      buildUserSchema({
        requireMlConsumerUserName: mlConsumerSelected,
        requireMlMortgageUserName: mlMortgageSelected,
      }),
    [mlConsumerSelected, mlMortgageSelected],
  );

  const { data } = useProducts();
  const tree = useMemo(() => buildProductTree(data ?? []), [data]);
  const [productAccess, setProductAccess] = useState<Record<string, boolean> | null>(
    user.productAccess,
  );
  const accessOn = productAccess !== null;
  const enabledUrns = Object.entries(state.products)
    .filter(([, on]) => on)
    .map(([urn]) => urn);

  const persistAccess = (access: Record<string, boolean> | null) => {
    setProductAccess(access);
    dispatch({ type: 'setUserAccess', id: user.id, access });
  };

  const handleAccessToggle = () => {
    // On -> always a fresh all-true record built from the current Step 3 set, never a reused or
    // stale one. Off -> always behind the confirmation, even with nothing deselected: the dialog
    // confirms the revert to "inherit all", not the loss of any particular selection.
    if (accessOn) {
      setConfirmingDiscard(true);
    } else {
      persistAccess(Object.fromEntries(enabledUrns.map((urn) => [urn, true])));
    }
  };

  const handleProductToggle = (urn: string) => {
    if (productAccess === null) return;
    persistAccess({ ...productAccess, [urn]: !productAccess[urn] });
  };

  const { register, watch, setValue, formState: { errors, isValid } } = useForm<UserFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      firstName: user.firstName,
      lastName: user.lastName,
      mlConsumerUserName: user.mlConsumerUserName,
      mlMortgageUserName: user.mlMortgageUserName,
      email: user.email,
      role: user.role === '' ? undefined : user.role,
    },
  });

  // Trimmed when each box loses focus (OM-4602): a trailing space after an address used to
  // make Email read "Invalid format" instead of simply coming off. Role is a select.
  const field = registerTrimmed(register, setValue);

  const values = watch();
  const email = values.email;

  // The scope for the duplication check (OM-3920 AC9: unique within the account being created).
  // The account's identity is Step 1's Lender ID — the future lender urn — since the naming
  // moved to the wizard; while it is still blank the check falls back to system-wide, erring
  // toward blocking.
  const lenderScope = state.account.lenderId?.trim().toLowerCase();

  // The same address typed on another row of this very wizard — no backend can see those yet.
  const crossCardDuplicate =
    !!email &&
    state.users.some(
      (u) => u.id !== user.id && u.email.trim().toLowerCase() === email.trim().toLowerCase(),
    );

  /**
   * OM-4359. The login becomes half of `urn:los:meridianlink:lender:<code>:user:<login>`, which
   * onboarding UPSERTS — so a repeat does not fail loudly, it silently repoints an existing
   * mapping at the wrong person. Compared lowercased because that urn lowercases both halves.
   *
   * Scoped to the lender, per OM-3920: two users of one lender cannot share a login, two
   * lenders can each have their own "jsmith". No backend probe is needed for the account being
   * created — Step 2 already refuses an ML Lender Code any other lender holds (checking legacy
   * identity urns too), so nothing can pre-exist under this lender's code and the only
   * reachable collision is between these cards.
   */
  const mlConsumerUserName = values.mlConsumerUserName?.trim().toLowerCase();
  const mlConsumerDuplicate =
    mlConsumerSelected &&
    !!mlConsumerUserName &&
    state.users.some(
      (u) =>
        u.id !== user.id &&
        u.mlConsumerUserName?.trim().toLowerCase() === mlConsumerUserName,
    );

  // Commits to the wizard state on every change, same as Step1Account does for the account form,
  // so closing the dialog never loses what was typed here.
  useEffect(() => {
    onChange({
      firstName: values.firstName,
      lastName: values.lastName,
      // Written back even while hidden: deselecting the LOS must not lose what was typed.
      mlConsumerUserName: values.mlConsumerUserName,
      mlMortgageUserName: values.mlMortgageUserName,
      email: values.email,
      role: values.role ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(values)]);

  useEffect(() => {
    let cancelled = false;
    if (!email || errors.email) {
      setDuplicate(false);
      setCheckedEmail(null);
      return;
    }
    if (crossCardDuplicate) {
      setDuplicate(true);
      setCheckedEmail(email);
      return;
    }
    checkUserEmailUnique(email, lenderScope || undefined)
      .then((unique) => { if (!cancelled) { setDuplicate(!unique); setCheckedEmail(email); } })
      .catch(() => { if (!cancelled) { setDuplicate(false); setCheckedEmail(email); } });
    return () => { cancelled = true; };
  }, [email, errors.email, crossCardDuplicate, lenderScope]);

  // Same guard as Step1Account: true from the instant the email becomes syntactically valid
  // until the async duplicate check for that exact value has landed — closes the window where
  // isValid is already true but duplicate hasn't been confirmed either way yet.
  const awaitingCheck = !!email && !errors.email && checkedEmail !== email;
  const canSave = isValid && !duplicate && !mlConsumerDuplicate && !awaitingCheck;

  // While the dialog is open the step's Next gate holds unconditionally (the overlay makes it
  // unreachable anyway). What matters is the verdict left behind on unmount: a row closed while
  // invalid, duplicate, or unchecked keeps blocking Next until it is edited again (AC9: "the
  // user remains on Step 4").
  const blockedRef = useRef(true);
  blockedRef.current = !canSave;
  useEffect(() => {
    onBlockedChange?.(true);
    return () => onBlockedChange?.(blockedRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true"
      aria-label={isNew ? ADD_USER_TITLE : EDIT_USER_TITLE}>
      <div className="fixed inset-0 bg-black/40" />
      <div className="card relative z-10 max-h-[90vh] w-full max-w-2xl overflow-y-auto p-6 lg:p-8">
        <div className="flex items-start justify-between">
          <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
            {isNew ? ADD_USER_TITLE : EDIT_USER_TITLE}
          </h2>
          <button type="button" aria-label="Close" className="btn-icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <h3 className="mt-4 text-sm font-semibold text-gray-700 dark:text-gray-300">User Information</h3>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <div className="field">
            <label className="label" htmlFor={`user-first-name-${user.id}`}>
              First Name <span className="text-red-500">*</span>
            </label>
            <input id={`user-first-name-${user.id}`} className="input" {...field('firstName')} />
            {errors.firstName && (
              <p className="text-sm text-red-500 mt-1">{errors.firstName.message}</p>
            )}
          </div>
          <div className="field">
            <label className="label" htmlFor={`user-last-name-${user.id}`}>
              Last Name <span className="text-red-500">*</span>
            </label>
            <input id={`user-last-name-${user.id}`} className="input" {...field('lastName')} />
            {errors.lastName && (
              <p className="text-sm text-red-500 mt-1">{errors.lastName.message}</p>
            )}
          </div>
          {mlConsumerSelected && (
            <div className="field sm:col-span-2">
              <label className="label" htmlFor={`user-ml-username-${user.id}`}>
                MeridianLink Consumer User Name <span className="text-red-500">*</span>
              </label>
              <input
                id={`user-ml-username-${user.id}`}
                className="input"
                {...field('mlConsumerUserName')}
              />
              {errors.mlConsumerUserName && (
                <p className="text-sm text-red-500 mt-1">{errors.mlConsumerUserName.message}</p>
              )}
              {!errors.mlConsumerUserName && mlConsumerDuplicate && (
                <p className="text-sm text-red-500 mt-1">{ML_CONSUMER_USER_NAME_DUPLICATE}</p>
              )}
            </div>
          )}
          {mlMortgageSelected && (
            <div className="field sm:col-span-2">
              <label className="label" htmlFor={`user-mlm-username-${user.id}`}>
                MeridianLink Mortgage User Name <span className="text-red-500">*</span>
              </label>
              <input
                id={`user-mlm-username-${user.id}`}
                className="input"
                {...field('mlMortgageUserName')}
              />
              {errors.mlMortgageUserName && (
                <p className="text-sm text-red-500 mt-1">{errors.mlMortgageUserName.message}</p>
              )}
            </div>
          )}
          <div className="field">
            <label className="label" htmlFor={`user-email-${user.id}`}>
              Email <span className="text-red-500">*</span>
            </label>
            <input id={`user-email-${user.id}`} className="input" {...field('email')} />
            {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>}
            {!errors.email && duplicate && (
              <p className="text-sm text-red-500 mt-1">{EMAIL_DUPLICATE}</p>
            )}
          </div>
          <div className="field">
            <label className="label" htmlFor={`user-role-${user.id}`}>
              User Role <span className="text-red-500">*</span>
            </label>
            <select id={`user-role-${user.id}`} className="input" {...register('role')}>
              <option value="">Select a role</option>
              {Object.entries(USER_ROLE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 border-t border-gray-200 pt-4 dark:border-gray-700">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Product Access</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-3">{STEP4_ACCESS_HINT}</p>
          <button
            type="button"
            role="switch"
            aria-checked={accessOn}
            aria-label={EDIT_PRODUCT_ACCESS_LABEL}
            onClick={handleAccessToggle}
            className="flex items-center gap-2"
          >
            <span
              className={`flex h-4 w-8 items-center rounded-full px-0.5 transition-colors ${
                accessOn ? 'bg-blue-600 dark:bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`h-3 w-3 rounded-full bg-white transition-transform ${
                  accessOn ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </span>
            <span className="text-sm text-gray-700 dark:text-gray-300">{EDIT_PRODUCT_ACCESS_LABEL}</span>
          </button>

          {accessOn && (
            <div className="mt-4">
              <div className="mb-3 flex items-start gap-2 rounded border border-blue-400 bg-blue-50 p-3 text-sm text-blue-800 dark:border-blue-500/60 dark:bg-blue-950/40 dark:text-blue-200">
                <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                <p>{STEP4_ACCESS_ON_TEXT}</p>
              </div>
              <ProductAccordionTree
                tree={tree}
                selected={productAccess ?? {}}
                onToggle={handleProductToggle}
                visibleUrns={enabledUrns}
              />
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button type="button" className="btn-primary" disabled={!canSave} onClick={onClose}>
            {isNew ? ADD_USER_TITLE : EDIT_USER_CONFIRM}
          </button>
        </div>
      </div>

      <WizardDialog
        open={confirmingDiscard}
        title={DISCARD_ACCESS_TITLE}
        body={DISCARD_ACCESS_BODY}
        confirmLabel={DISCARD_ACCESS_CONFIRM}
        cancelLabel={DISCARD_ACCESS_CANCEL}
        onConfirm={() => { setConfirmingDiscard(false); persistAccess(null); }}
        onCancel={() => setConfirmingDiscard(false)}
      />
    </div>
  );
}
